import { A } from '@solidjs/router';
import { createEffect, onCleanup } from 'solid-js';
import PublicLayout from './PublicLayout';
import { t, locale } from '@/shared/i18n';
import { usePublicPath } from '@/shared/lib/usePublicPath';

export default function TermsOfServicePage() {
  createEffect(() => {
    const lang = locale() === 'vi' ? 'vi' : 'en';
    const path = lang === 'vi' ? '/vi/terms' : '/terms';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: t('terms.title'),
      description: t('seo.terms.desc'),
      inLanguage: lang,
      url: `https://khoai.io${path}`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'khoai.io',
        url: 'https://khoai.io',
      },
    };

    let script = document.getElementById(
      'ld-json-terms-of-service'
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'ld-json-terms-of-service';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
  });

  onCleanup(() => {
    document.getElementById('ld-json-terms-of-service')?.remove();
  });

  const link = 'text-blue-600 hover:underline';
  const sectionCls = 'mt-10';
  const h2Cls = 'text-xl font-bold text-text-primary';
  const pCls = 'mt-3 text-sm leading-relaxed text-text-secondary';
  const p2Cls = 'mt-2 text-sm leading-relaxed text-text-secondary';
  const ulCls = 'mt-3 list-disc space-y-1 pl-6 text-sm text-text-secondary';
  const ul2Cls = 'mt-2 list-disc space-y-1 pl-6 text-sm text-text-secondary';
  const pp = usePublicPath();

  return (
    <PublicLayout>
      <article class="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <h1 class="text-3xl font-extrabold text-text-primary sm:text-4xl">
          {t('terms.title')}
        </h1>
        <p class="mt-2 text-sm text-text-secondary">{t('terms.effective')}</p>

        {/* 1 */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s1.title')}</h2>
          <p class={pCls}>{t('terms.s1.p1')}</p>
        </section>

        {/* 2 */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s2.title')}</h2>
          <p class={pCls}>
            {t('terms.s2.p1')}{' '}
            <a href="https://khoai.io" class={link}>
              khoai.io
            </a>
            .
          </p>
        </section>

        {/* 3 */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s3.title')}</h2>
          <ul class={ulCls}>
            <li>{t('terms.s3.li1')}</li>
            <li>{t('terms.s3.li2')}</li>
            <li>{t('terms.s3.li3')}</li>
            <li>{t('terms.s3.li4')}</li>
          </ul>
        </section>

        {/* 4 */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s4.title')}</h2>
          <p class={pCls}>{t('terms.s4.p1')}</p>
          <ul class={ul2Cls}>
            <li>{t('terms.s4.li1')}</li>
            <li>{t('terms.s4.li2')}</li>
            <li>{t('terms.s4.li3')}</li>
            <li>{t('terms.s4.li4')}</li>
            <li>{t('terms.s4.li5')}</li>
            <li>{t('terms.s4.li6')}</li>
          </ul>
        </section>

        {/* 5 */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s5.title')}</h2>
          <p class={pCls}>{t('terms.s5.p1')}</p>
          <p class={p2Cls}>{t('terms.s5.p2')}</p>
        </section>

        {/* 6 */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s6.title')}</h2>
          <p class={pCls}>{t('terms.s6.p1')}</p>
          <p class={p2Cls}>
            {t('terms.s6.p2')}{' '}
            <A href={pp('/privacy')} class={link}>
              {t('public.footer.privacy')}
            </A>
            .
          </p>
        </section>

        {/* 7 */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s7.title')}</h2>
          <ul class={ulCls}>
            <li>
              {t('terms.s7.li1')}{' '}
              <A href={pp('/pricing')} class={link}>
                {t('public.nav.pricing')}
              </A>{' '}
              {t('terms.s7.li1.after')}
            </li>
            <li>{t('terms.s7.li2')}</li>
            <li>{t('terms.s7.li3')}</li>
            <li>{t('terms.s7.li4')}</li>
            <li>{t('terms.s7.li5')}</li>
            <li>{t('terms.s7.li6')}</li>
          </ul>
          <p class={p2Cls}>
            {t('terms.s7.p1')}{' '}
            <A href={pp('/billing-terms')} class={link}>
              {t('public.footer.billingTerms')}
            </A>
            .
          </p>
        </section>

        {/* 8 */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s8.title')}</h2>
          <ul class={ulCls}>
            <li>{t('terms.s8.li1')}</li>
            <li>{t('terms.s8.li2')}</li>
            <li>{t('terms.s8.li3')}</li>
            <li>
              {t('terms.s8.li4')}{' '}
              <a href="mailto:human.khoai@gmail.com" class={link}>
                contact us
              </a>{' '}
              {t('terms.s8.li4.after')}
            </li>
          </ul>
        </section>

        {/* 9 */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s9.title')}</h2>
          <p class={pCls}>{t('terms.s9.p1')}</p>
          <p class={p2Cls}>{t('terms.s9.p2')}</p>
        </section>

        {/* 10 */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s10.title')}</h2>
          <p class={pCls}>{t('terms.s10.p1')}</p>
          <p class={p2Cls}>{t('terms.s10.p2')}</p>
          <p class={p2Cls}>{t('terms.s10.p3')}</p>
        </section>

        {/* 11 — Indemnification (NEW) */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s11.title')}</h2>
          <p class={pCls}>{t('terms.s11.p1')}</p>
        </section>

        {/* 12 — Force Majeure (NEW) */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s12.title')}</h2>
          <p class={pCls}>{t('terms.s12.p1')}</p>
        </section>

        {/* 13 — Account Termination */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s13.title')}</h2>
          <ul class={ulCls}>
            <li>{t('terms.s13.li1')}</li>
            <li>{t('terms.s13.li2')}</li>
            <li>{t('terms.s13.li3')}</li>
          </ul>
        </section>

        {/* 14 — Governing Law & Dispute Resolution */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s14.title')}</h2>
          <p class={pCls}>{t('terms.s14.p1')}</p>
          <p class={p2Cls}>{t('terms.s14.p2')}</p>
        </section>

        {/* 15 — Assignment (NEW) */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s15.title')}</h2>
          <p class={pCls}>{t('terms.s15.p1')}</p>
        </section>

        {/* 16 — Changes */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s16.title')}</h2>
          <p class={pCls}>{t('terms.s16.p1')}</p>
        </section>

        {/* 17 — Severability (NEW) */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s17.title')}</h2>
          <p class={pCls}>{t('terms.s17.p1')}</p>
        </section>

        {/* 18 — Entire Agreement (NEW) */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s18.title')}</h2>
          <p class={pCls}>{t('terms.s18.p1')}</p>
          <p class={p2Cls}>{t('terms.s18.p2')}</p>
        </section>

        {/* 19 — Contact */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('terms.s19.title')}</h2>
          <p class={pCls}>{t('terms.s19.p1')}</p>
          <p class="mt-2 text-sm text-text-secondary">
            <a href="mailto:human.khoai@gmail.com" class={link}>
              contact us
            </a>
          </p>
        </section>
      </article>
    </PublicLayout>
  );
}
